import { Service } from '@/api/apiServices';
import { Badge } from '@/components/ui/badge';
import { renderCategoryPills } from '@/components/ui/table-pill';
import { ColumnDef } from '@tanstack/react-table';
import ServiceBox from '../home/serviceslist/ServiceBox';
import { ServiceDataTableRowActions } from './service-data-table-row-actions';

interface ColumnsProps {
  onEdit: (service: Service) => void;
  onDelete: (id: number) => void;
  onSwapPosition: (currentRow: Service, targetRow: Service, direction: 'up' | 'down') => void;
}

export const columns = ({
  onEdit,
  onDelete,
  onSwapPosition,
}: ColumnsProps): ColumnDef<Service>[] => {
  return [
    {
      id: 'actions',
      size: 44,
      minSize: 44,
      maxSize: 44,
      cell: ({ row, table }) => (
        <div className="flex w-full items-center justify-center">
          <ServiceDataTableRowActions
            service={row.original}
            table={table}
            onEdit={onEdit}
            onDelete={onDelete}
            onSwapPosition={onSwapPosition}
          />
        </div>
      ),
    },
    {
      accessorKey: 'serAvatar',
      header: 'Avatar',
      size: 72,
      minSize: 72,
      maxSize: 72,
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          <ServiceBox
            className="service-box--table"
            avatar={{
              shape: row.original.serAvatar?.shape || 'square',
              color1: row.original.serAvatar?.color1 || '#F5E6CA',
              color2: row.original.serAvatar?.color2 || '#ffffff',
            }}
            title={row.original.serTitle}
            description={row.original.serDescription}
            disableTooltip
          />
        </div>
      ),
    },
    {
      accessorKey: 'serTitle',
      header: 'Title',
      size: 160,
      minSize: 120,
      cell: ({ row }) => (
        <span className="block truncate text-sm font-medium">{row.getValue('serTitle')}</span>
      ),
    },
    {
      accessorKey: 'serDescription',
      header: 'Desc',
      size: 64,
      minSize: 56,
      maxSize: 80,
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">{row.getValue('serDescription')}</span>
      ),
    },
    {
      accessorKey: 'serCredit',
      header: 'Credit',
      size: 64,
      minSize: 56,
      maxSize: 80,
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">{row.getValue('serCredit')}</span>
      ),
    },
    {
      accessorKey: 'serSupply',
      header: 'Supply',
      size: 64,
      minSize: 56,
      maxSize: 80,
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">{row.getValue('serSupply')}</span>
      ),
    },
    {
      accessorKey: 'serCategory',
      header: 'Category',
      size: 100,
      minSize: 88,
      cell: ({ row }) => renderCategoryPills(row.getValue('serCategory')),
    },
    {
      accessorKey: 'serSpecialCategory',
      header: 'Special',
      size: 100,
      minSize: 88,
      cell: ({ row }) => renderCategoryPills(row.getValue('serSpecialCategory')),
    },
    {
      accessorKey: 'serAmount',
      header: 'Price',
      size: 64,
      minSize: 56,
      maxSize: 80,
      cell: ({ row }) => (
        <span className="text-sm font-medium tabular-nums">{row.getValue('serAmount')}</span>
      ),
    },
    {
      accessorKey: 'serStatus',
      header: 'Status',
      size: 88,
      minSize: 80,
      maxSize: 100,
      cell: ({ row }) => {
        const isActive = Boolean(row.getValue('serStatus'));
        return isActive ? (
          <Badge
            className="rounded-full border-0 bg-[#B56A3A] px-2.5 py-0.5 text-xs font-medium text-white hover:bg-[#B56A3A]"
            size="small"
          >
            Active
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="rounded-full px-2.5 py-0.5 text-xs font-medium"
            size="small"
          >
            Inactive
          </Badge>
        );
      },
    },
  ];
};
